// Auth store — uchovává přihlášeného uživatele a JWT token.
// Token se zároveň ukládá do localStorage pod klíčem 'cw_auth_token',
// aby ho mohl číst api/client.js bez importu Zustand.
// Při každé změně uživatele (login/logout) se:
// 1. invaliduje celá React Query cache (všechny queries se refetchují s novým auth)
// 2. vyčistí se data stanice (selectedStation) ze stationu storu
// Výjimka: vypršení přihlášení (sessionExpired) kontext nemaže — když se pak znovu
// přihlásí stejný uživatel, pokračuje se stejnou stanicí (viz doc/plans/session-expiry.md).
import { create } from 'zustand'
import { queryClient } from '../api/queryClient'
import { useStationStore } from './stationStore'
import { useOnlineModeStore } from './useOnlineModeStore'
import { useFillAttemptStore } from './useFillAttemptStore'
import { useIncompleteDataStore } from './useIncompleteDataStore'
import { useDeployConfigStore } from './useDeployConfigStore'
import { useLoginModalStore } from './useLoginModalStore'
import { authApi } from '../api/auth'
import { setSessionExpiredHandler } from '../api/client'

const TOKEN_KEY = 'cw_auth_token'

export const useAuthStore = create((set, get) => ({
  token:        localStorage.getItem(TOKEN_KEY) ?? null,
  // id uživatele, kterému vypršelo přihlášení — při jeho opětovném přihlášení se
  // kontext (stanice, režim dotahování…) zachová. null = žádné vypršení.
  expiredUserId: null,
  user:         null,     // { id, username, display_name, first_name, last_name, email, is_admin, access_source, online_source }
  accessSource: 'open',   // co uživatel smí ČÍST — 'open' | 'oracle'
  onlineSource: 'none',   // odkud smí online DOTAHOVAT — 'none' | 'open' | 'oracle'
  canFill:      false,    // odvozeno: onlineSource !== 'none' — smí vůbec spustit dotahování
  // Co uživatel smí vidět na téhle instanci — server je tvrdá hranice (viz cw-server
  // ČÁST C + doc/plans/deploy-variants-cw-server-handoff.md), klient to jen zrcadlí,
  // aby nezobrazoval mrtvá tlačítka. Zdroj: capabilities / me / login (visible_data_categories,
  // can_see_charts) — hodnota už je serverem spočtená pro (role × varianta nasazení).
  visibleDataCategories: ['SDATA', 'SNDATA', 'SEDATA'],
  canSeeCharts:          false,

  setAuth: (token, user) => {
    localStorage.setItem(TOKEN_KEY, token)
    // Znovupřihlášení téhož uživatele po vypršení → kontext zůstává, jen se data znovu načtou.
    const keepContext = get().expiredUserId != null && get().expiredUserId === user?.id
    if (!keepContext) {
      // Vyčistit cached data stanice — bude se muset znova načíst s novým tokenem
      useStationStore.getState().clearSelectedStation()
      // Nový uživatel začíná na výchozím režimu dotahování ('ask'), nedědí volbu předchozího.
      useOnlineModeStore.getState().reset()
      // Jiný uživatel = jiná práva dotahování → zahodit cache pokusů o dotažení.
      useFillAttemptStore.getState().clear()
      useIncompleteDataStore.getState().clear()
    }
    // Invalidovat všechny queries — aktivní queries (včetně elements) se okamžitě re-fetchnou
    // s novým tokenem — observer zůstane živý, data nezůstanou prázdná.
    queryClient.invalidateQueries()
    set({ token, user, expiredUserId: null, ...deriveAccess(user) })
  },

  // Obnovený token (POST /auth/refresh) — stejný uživatel, stejná práva → bez invalidace.
  replaceToken: (token) => {
    localStorage.setItem(TOKEN_KEY, token)
    set({ token })
  },

  // Vypršelo přihlášení (server vrátil session_expired, nebo klient zjistil prošlé `exp`).
  // Na rozdíl od logout() nemaže kontext ani neinvaliduje queries — otevře login modal;
  // po znovupřihlášení téhož uživatele setAuth jen znovu načte data. Když uživatel modal
  // zavře bez přihlášení, LoginModal zavolá dismissSessionExpired() → pokračuje jako anonym.
  sessionExpired: (message) => {
    const { token, user } = get()
    if (!token) return
    localStorage.removeItem(TOKEN_KEY)
    set({ token: null, user: null, expiredUserId: user?.id ?? null, ...deriveAccess(null) })
    authApi.capabilities()
      .then(c => set(deriveAccess(c)))
      .catch(() => {})
    useLoginModalStore.getState().show(
      'session_expired',
      message || 'Platnost přihlášení vypršela. Přihlaste se prosím znovu.',
    )
  },

  // Login modal po vypršení zavřen bez přihlášení → data znovu načíst jako anonym.
  dismissSessionExpired: () => {
    if (get().token) return
    set({ expiredUserId: null })
    useFillAttemptStore.getState().clear()
    useIncompleteDataStore.getState().clear()
    queryClient.invalidateQueries()
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY)
    // Vyčistit cached data stanice — bude se muset znova načíst jako anonymní
    useStationStore.getState().clearSelectedStation()
    // Odhlášení = zpět na výchozí režim dotahování ('ask').
    useOnlineModeStore.getState().reset()
    useFillAttemptStore.getState().clear()
    useIncompleteDataStore.getState().clear()
    // Invalidovat všechny queries — token je už z localStorage pryč, takže re-fetch proběhne
    // jako anonymní uživatel — aktivní queries se okamžitě obnoví se správnými daty.
    queryClient.invalidateQueries()
    set({ token: null, user: null, expiredUserId: null, ...deriveAccess(null) })
    // deriveAccess(null) nevolá setConfig (žádný src.deploy) — deployConfig (menuIcons atd.)
    // by tak zůstal na hodnotách přihlášeného uživatele (admin) až do refreshe. Znovu natáhnout
    // capabilities pro anonymní roli, aby menu/ikony/panely zmizely hned po odhlášení.
    authApi.capabilities()
      .then(c => set(deriveAccess(c)))
      .catch(() => {})
  },

  setUser: (user) => set({ user, ...deriveAccess(user) }),

  // Nastaví osy práv z GET /api/auth/capabilities (anonym i přihlášený) — user beze změny.
  setCapabilities: (caps) => set(deriveAccess(caps)),
}))

// Z objektu s poli access_source / online_source / visible_data_categories / can_see_charts /
// deploy (user z login/me, nebo přímo capabilities odpověď) odvodí accessSource / onlineSource /
// canFill / visibleDataCategories / canSeeCharts. Vedlejší efekt: `deploy` blok (pokud přišel)
// uloží i do useDeployConfigStore — jedno volání /api/auth/capabilities tak sytí oba story.
// null / undefined → anonymní default (nic navíc).
function deriveAccess(src) {
  const accessSource = src?.access_source ?? 'open'
  const onlineSource = src?.online_source ?? 'none'
  if (src?.deploy) useDeployConfigStore.getState().setConfig(src.deploy)
  return {
    accessSource,
    onlineSource,
    canFill: onlineSource !== 'none',
    // Server posílá vždy explicitní hodnoty (i pro anonyma) — fallback se použije
    // jen když chybí (starší server / výpadek), viz useDeployConfigStore výchozí stav.
    visibleDataCategories: src?.visible_data_categories ?? ['SDATA', 'SNDATA', 'SEDATA'],
    canSeeCharts:          src?.can_see_charts ?? false,
  }
}

// Anonym = žádný token a žádný načtený user. Používá se pro UI gate (grafy, menu).
export function selectIsAnonymous(s) {
  return !s.user && !s.token
}

// api/client.js hlásí 401 session_expired přes tento handler (nemůže importovat store).
setSessionExpiredHandler((message) => useAuthStore.getState().sessionExpired(message))
